'use client';

import SettingConfig from '@module/config/setting';
import { cn } from '@module/lib/utils';
import useModalStore from '@module/stores/useModalStore';
import { IdentityEasy, IdentityIpin, IdentityMobile } from '@module/svg/icons';
import type { AuthMode, AuthType, IdentityVerificationButton } from '@module/types/modal/auth/type';

interface IdentityVerificationButtonGroupProps {
  popupWidth?: number;
  popupHeight?: number;
  postMessage: string;
  authMode?: AuthMode;
  containerClassName?: string;
  buttonClassName?: string;
  excludedType?: AuthType[];
  isShowIcon?: boolean;
  setAuthType?: (type: AuthType) => void;
}

function IdentityVerificationButtonGroup({
  popupWidth = 480,
  popupHeight = 812,
  postMessage,
  authMode,
  containerClassName,
  buttonClassName,
  excludedType,
  isShowIcon = true,
  setAuthType,
}: IdentityVerificationButtonGroupProps) {
  const { pushModal, popModal } = useModalStore();

  const openAuthUrl = (authType: AuthType) => {
    const top = window.screenY + window.outerHeight / 2 - popupHeight / 2;
    const left = window.screenX + window.outerWidth / 2 - popupWidth / 2;

    const url = new URL(`${SettingConfig.API_URL}`);
    url.pathname = '/resource/auth/index.php';
    url.searchParams.append('authType', authType);
    url.searchParams.append('postMessage', postMessage);
    if (authMode) {
      url.searchParams.append('authMode', authMode);
    }

    window.open(
      url,
      'IdentityVerification',
      `popup=yes,width=${popupWidth},height=${popupHeight},top=${top},left=${left},toolbar=no,menubar=no`,
    );
  };

  const onClickHandler = (authType: AuthType) => {
    setAuthType?.(authType);
    if (postMessage === 'initialAuth' && authType === 'ipin') {
      pushModal({
        type: 'CONFIRM_CANCEL_MODAL',
        options: {
          description: (
            <>
              <span>
                <b>휴대폰 인증</b> 시 무료 하트 기회를
                <br />
                안내 받으실 수 있습니다.
              </span>
              <br />
              <div className='mt-4 mb-2 text-[#777] text-[18px]'>아이핀 인증을 진행 하시겠습니까?</div>
            </>
          ),
          onClick: () => {
            openAuthUrl(authType);
            popModal();
          },
        },
      });
      return;
    }
    openAuthUrl(authType);
  };

  const buttonList: (IdentityVerificationButton | null)[] = [
    excludedType?.includes('phone') ? null : { type: 'phone', title: '휴대폰 인증', img: <IdentityMobile /> },
    excludedType?.includes('ipin') ? null : { type: 'ipin', title: '아이핀 인증', img: <IdentityIpin /> },
    excludedType?.includes('easy') ? null : { type: 'easy', title: '간편 인증', img: <IdentityEasy /> },
  ];

  return (
    <div className={cn('flex gap-2', containerClassName)}>
      {buttonList.map((el, i) => {
        if (!el) return null;
        return (
          <button
            key={i}
            type='button'
            onClick={() => onClickHandler(el.type)}
            data-gtm-auth-method-btn={el.type}
            className={cn(
              'flex h-[72px] w-[240px] flex-col items-center justify-center gap-1 rounded-[5px] border border-borderBg13 text-baseFg text-sm',
              buttonClassName,
            )}
          >
            {isShowIcon && el.img}
            {el.title}
          </button>
        );
      })}
    </div>
  );
}

export default IdentityVerificationButtonGroup;
